import React, { ReactNode, useReducer } from 'react';

const SET_ACTIVE_TAB = 'SET_ACTIVE_TAB';
const SET_COUNTER = 'SET_COUNTER';
const SET_IS_GOOGLE_MAP_ADDRESS_VALID = 'SET_IS_GOOGLE_MAP_ADDRESS_VALID';
const NEW_RENTAL_ID = 'NEW_RENTAL_ID';

export interface IPropertyStateFields {
  activeTab: number;
  counter: number;
  isGoogleMapAddressValid: boolean;
  newRentalId?: string | null;
  // Derived from the URL by PropertyRouteProvider; undefined outside rentals/:id/*.
  propertyId?: string;
  propertyCode?: string;
  propertyName?: string;
  objectType?: number;
  unitId?: string;
  unitCode?: string;
  unitName?: string;
  unitType?: number;
  isEditMode: boolean;
  isGlobalEditMode: boolean;
  hasUnits: boolean;
}

export interface IPropertyStateContext {
  state: IPropertyStateFields;
  setActiveTab: (tabId: number) => void;
  setCounter: (counterValue: number) => void;
  setIsGoogleMapAddressValid: (isAddressValid: boolean) => void;
  setNewRentalId: (id?: string | null) => void;
}

const initialState: IPropertyStateFields = {
  activeTab: 1,
  counter: 0,
  isGoogleMapAddressValid: true,
  newRentalId:
    window.localStorage.getItem(NEW_RENTAL_ID) !== 'undefined'
      ? window.localStorage.getItem(NEW_RENTAL_ID)
      : undefined,
  isEditMode: false,
  isGlobalEditMode: false,
  hasUnits: false,
};

export const PropertyState = React.createContext<IPropertyStateContext>({
  state: { ...initialState },
  setActiveTab: () => {},
  setCounter: () => {},
  setIsGoogleMapAddressValid: () => {},
  setNewRentalId: () => {},
});

type TAction =
  | { type: typeof SET_ACTIVE_TAB; tabId: number }
  | { type: typeof SET_COUNTER; counterValue: number }
  | { type: typeof SET_IS_GOOGLE_MAP_ADDRESS_VALID; isAddressValid: boolean }
  | { type: typeof NEW_RENTAL_ID; id?: string | null };

const reducer = (state: IPropertyStateFields = initialState, action: TAction) => {
  switch (action.type) {
    case SET_ACTIVE_TAB:
      return { ...state, activeTab: action.tabId };
    case SET_COUNTER:
      return { ...state, counter: action.counterValue };
    case SET_IS_GOOGLE_MAP_ADDRESS_VALID:
      return { ...state, isGoogleMapAddressValid: action.isAddressValid };
    case NEW_RENTAL_ID:
      return { ...state, newRentalId: action.id };

    default:
      return state;
  }
};

const PropertyProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const setActiveTab = (tabId: number) => {
    dispatch({ type: SET_ACTIVE_TAB, tabId });
  };

  const setCounter = (counterValue: number) => {
    dispatch({ type: SET_COUNTER, counterValue });
  };

  const setIsGoogleMapAddressValid = (isAddressValid: boolean) => {
    dispatch({ type: SET_IS_GOOGLE_MAP_ADDRESS_VALID, isAddressValid });
  };

  const setNewRentalId = (id?: string | null) => {
    window.localStorage.setItem(NEW_RENTAL_ID, id as string);
    dispatch({ type: NEW_RENTAL_ID, id });
  };

  const value: IPropertyStateContext = {
    state,
    setActiveTab,
    setCounter,
    setIsGoogleMapAddressValid,
    setNewRentalId,
  };

  return <PropertyState.Provider value={value}>{children}</PropertyState.Provider>;
};

export default PropertyProvider;
